import { Fragment, useEffect, useRef, useState, memo, useCallback, Activity, useMemo } from "react";
import {
  Breadcrumbs,
  Button,
  Chip,
  Division,
  Drawer,
  Flex,
  Icon,
  Icons,
  motion,
  useNotification,
} from "@ir/ui";
import { useConfirm, useKeyboard } from "@/hooks";

import { Direction, useCredit, useDeductionDrawer, useDeductionEdit, useDeductionFilter, useDeductionNavigation } from "@/presentation/deduction/hooks";

import "./DeductionDrawer.scss";
import ProgressBar from "@/presentation/deduction/components/DrawerProgressBar";
import useDeductionIncomplete from "./DeductionIncomplete/hook/useDeductionIncomplete";
import { TabType } from "@/store/deduction/drawer";
import DeductionContent from "./DeductionContent";
import { HasAnyPermission } from "@/presentation/workspace";
import { WorkspacePermissions } from "@/core/permissions";
import CreditContent from "./Credit/CreditContent";
import { repairSearchFocusBlockers } from "@/presentation/deduction/components/debugSearchFocus";

const CHIP_COLOR = {
  "credit": "secondary",
  "deduction": "error",
} as const;

const DeductionDrawer = memo(() => {
  const {
    drawerInfo,
    clearDrawerInfo,
    isDrawerOpen,
    setDrawerOpen,
    isFirstItem,
    isLastItem,
    setRemittanceItemId,
    getDeductions,
    isNextPrevEnabled,
    setIsNextPrevEnabled,
    isDeductionsLoading,
    isDeductionsDetailsUpdating,
    isDeductionsIntelligenceUpdating,
    isDeductionsChargeBreakdownUpdating,
    clearViewerDocuments,
    isDocumentViewerOpen,
    setHasAnyUnsavedChanges,
    hasAnyUnsavedChanges,
    setIsDocumentViewerOpen,
    clearCurrentExpandedDeductionId,
    selectedTab,
    setSelectedTab,
  } = useDeductionDrawer();
  const { isLoadingDeductionStatus, clearAllData, getDeductionStatus } = useDeductionIncomplete();
  const { quickFilterInfo } = useDeductionFilter();
  const { setEditMode } = useDeductionEdit();
  const { handleDeductionNavigation: deductionNavigation } = useDeductionNavigation();
  const confirm = useConfirm();
  const [, contextHolder] = useNotification({ motion, closable: true });
  const { getCredit, resetSelectedTab } = useCredit();

  const previousButtonRef = useRef<HTMLButtonElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);
  const wasDrawerOpenRef = useRef(false);

  const [lastNavigationDirection, setLastNavigationDirection] = useState<Direction | null>(null);

  useEffect(() => {
    if (!isDeductionsLoading && !isLoadingDeductionStatus && lastNavigationDirection && isNextPrevEnabled) {
      if (lastNavigationDirection === Direction.NEXT) {
        nextButtonRef.current?.focus();
      } else if (lastNavigationDirection === Direction.PREVIOUS) {
        previousButtonRef.current?.focus();
      }
    }
  }, [isDeductionsLoading, isLoadingDeductionStatus, lastNavigationDirection, isNextPrevEnabled]);

  const showUnSavedConfirmation = useCallback(async (): Promise<boolean> => {
    if (hasAnyUnsavedChanges) {
      const confirmResult = await confirm({
        title: "Unsaved Changes",
        message: "You have unsaved updates. Leave without saving?",
        confirmText: "Discard",
        cancelText: "Go Back",
      });
      if (confirmResult) {
        setHasAnyUnsavedChanges(false);
      }
      return confirmResult;
    }
    return true;
  }, [hasAnyUnsavedChanges, confirm, setHasAnyUnsavedChanges]);

  useKeyboard({
    keys: ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Escape"],
    onKeyDown: e => {
      if (!isDrawerOpen) return;
      if (e.key === "Escape") {
        handleCloseDrawer();
        return;
      }
      if (!isNextPrevEnabled || isDeductionsLoading) return;
      const isNext = e.key === "ArrowDown" || e.key === "ArrowRight";
      if (isNext) {
        handleDeductionNavigation(Direction.NEXT);
      } else {
        handleDeductionNavigation(Direction.PREVIOUS);
      }
    },
  });

  const resetDrawerState = useCallback(() => {
    clearDrawerInfo();
    clearCurrentExpandedDeductionId();
    setSelectedTab(TabType.INFORMATION);
    resetSelectedTab();
    setIsDocumentViewerOpen(false);
    clearViewerDocuments();
    clearAllData();
  }, [
    clearDrawerInfo,
    clearCurrentExpandedDeductionId,
    setSelectedTab,
    resetSelectedTab,
    setIsDocumentViewerOpen,
    clearViewerDocuments,
    clearAllData,
  ]);

  const handleCloseDrawer = useCallback(async () => {
    const confirm = await showUnSavedConfirmation();
    if (!confirm) return;

    // Blur before closing. After a browser tab switch, React Aria FocusScope can
    // try createTreeWalker on a detached parentElement (null) during containment
    // restore — that exception leaves page focus broken (search input unfocusable).
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    setHasAnyUnsavedChanges(false);
    setDrawerOpen(false);
  }, [showUnSavedConfirmation, setDrawerOpen, setHasAnyUnsavedChanges]);

  // Clear drawer content only after ModalOverlay/FocusScope finish exiting.
  // Clearing synchronously with isOpen=false races FocusScope teardown in prod.
  useEffect(() => {
    if (isDrawerOpen) {
      wasDrawerOpenRef.current = true;
      return;
    }

    if (!wasDrawerOpenRef.current) {
      return;
    }
    wasDrawerOpenRef.current = false;

    const timeoutId = window.setTimeout(() => {
      resetDrawerState();
      // Drawer ModalOverlay uses ariaHideOutside({ shouldUseInert: true }).
      // After a tab-switch race, inert/aria-hidden can remain on the app shell
      // and make the search input unfocusable with no visible overlay.
      repairSearchFocusBlockers();
    }, 250);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isDrawerOpen, resetDrawerState]);

  useEffect(() => {
    return () => {
      setDrawerOpen(false);
      resetDrawerState();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (drawerInfo?.remittanceItem?.isDeduction) {
      const triggerDeductions = () => {
        if (drawerInfo?.remittanceItemId) {
          setRemittanceItemId(drawerInfo.remittanceItemId);
          getDeductions(drawerInfo.isDeductionIncomplete ?? quickFilterInfo.statusId === "incomplete");
          if (drawerInfo.isDeductionIncomplete ?? quickFilterInfo.statusId === "incomplete") {
            getDeductionStatus();
          }
        }
      };
      triggerDeductions();
    }

    if (drawerInfo?.remittanceItem?.isCredit) {
      const triggerCredit = () => {
        if (drawerInfo?.remittanceItemId) {
          setRemittanceItemId(drawerInfo.remittanceItemId);
          getCredit(drawerInfo.remittanceItemId);
        }
      };
      triggerCredit();
    }


  }, [
    drawerInfo?.remittanceItemId,
    drawerInfo?.isDeductionIncomplete,
    quickFilterInfo.statusId,
    setRemittanceItemId,
    getDeductions,
    getDeductionStatus,
    drawerInfo?.remittanceItem?.isDeduction,
    drawerInfo?.remittanceItem?.isCredit,
    getCredit
  ]);

  useEffect(() => {
    if (isDrawerOpen && !selectedTab) {
      setSelectedTab(TabType.INFORMATION);
    }
  }, [isDrawerOpen, selectedTab, setSelectedTab]);

  const handleDeductionNavigation = async (direction: Direction) => {
    const confirm = await showUnSavedConfirmation();
    if (!confirm) return;
    clearViewerDocuments();
    clearCurrentExpandedDeductionId();
    setIsDocumentViewerOpen(false);
    deductionNavigation(direction);
    setLastNavigationDirection(direction);
    setIsNextPrevEnabled(true);
    clearAllData();
  };

  const breadcrumbsItems = useMemo(() => {
    return [
      { label: "Check #" + drawerInfo?.checkNumber },
      { label: drawerInfo?.invoiceNumber as string },
    ];
  }, [drawerInfo?.checkNumber, drawerInfo?.invoiceNumber]);

  const chipColor = useCallback((type: "credit" | "deduction") => {
    return CHIP_COLOR[type];
  }, []);


  const renderHeader = (isDocumentViewerOpen: boolean) => {
    return (
      <Flex
        align="center"
        justifyContent="space-between"
        className="bg-white px-4 pt-3 pb-2 drawer-header-content"
        style={{ borderBottom: "3px solid #fff", marginLeft: isDocumentViewerOpen ? "50%" : "0" }}
      >
        {(isDeductionsLoading ||
          isLoadingDeductionStatus ||
          isDeductionsDetailsUpdating ||
          isDeductionsIntelligenceUpdating ||
          isDeductionsChargeBreakdownUpdating) && <ProgressBar />}
        <Division>
          <Flex align="center" gap={0.5}>
            <Breadcrumbs items={breadcrumbsItems} />
            <Chip
              size="small"
              color={chipColor(drawerInfo?.remittanceItem?.isCredit ? "credit" : "deduction")}
              label={drawerInfo?.remittanceItem?.isCredit ? "Credit" : "Deduction"}
              variant="filled"
            />
          </Flex>
        </Division>
        <Flex align="center">
          <Activity mode={drawerInfo?.nextPrevEnabled ? "visible" : "hidden"}>
            <Button
              ref={previousButtonRef}
              size="medium"
              variant="clear"
              color="grey"
              className={(isFirstItem ?? isDeductionsLoading) ? "text-grey-200" : "text-grey-500"}
              onPress={() => handleDeductionNavigation(Direction.PREVIOUS)}
              isDisabled={isFirstItem ?? isDeductionsLoading ?? isLoadingDeductionStatus}
            >
              <Icon name={Icons.ChevronLeft} size="medium" />
            </Button>

            <Button
              ref={nextButtonRef}
              size="medium"
              variant="clear"
              color="grey"
              className={(isLastItem ?? isDeductionsLoading) ? "text-grey-200" : "text-grey-500"}
              onPress={() => handleDeductionNavigation(Direction.NEXT)}
              isDisabled={isLastItem ?? isDeductionsLoading ?? isLoadingDeductionStatus}
            >
              <Icon name={Icons.ChevronRight} size="medium" />
            </Button>
          </Activity>
          <Button size="medium" variant="clear" color="grey" onPress={handleCloseDrawer} className="text-grey-500">
            <Icon name={Icons.Close} size="medium" />
          </Button>
        </Flex>
      </Flex>
    );
  };


  useEffect(() => {
    setEditMode(null);
  }, [drawerInfo?.remittanceItemId, setEditMode]);

  return (
    <Fragment>
      <Drawer
        key="deduction-drawer"
        className="deduction-drawer"
        isOpen={isDrawerOpen}
        mask={false}
        size="large"
        width={isDocumentViewerOpen ? "100%" : "50vw"}
        isKeyboardDismissDisabled={true}
        header={renderHeader(isDocumentViewerOpen)}
      >
        <HasAnyPermission requiredPermissions={[WorkspacePermissions.DEDUCTIONS_VIEW]}>
          <Activity mode={drawerInfo?.remittanceItem?.isDeduction ? "visible" : "hidden"}>
            <DeductionContent />
          </Activity>
        </HasAnyPermission>
        <HasAnyPermission requiredPermissions={[WorkspacePermissions.CREDITS_VIEW]}>
        <Activity mode={drawerInfo?.remittanceItem?.isCredit ? "visible" : "hidden"}>
          <CreditContent />
        </Activity>
        </HasAnyPermission>
      </Drawer>
      {contextHolder}
    </Fragment>
  );
});

DeductionDrawer.displayName = "DeductionDrawer";

export default DeductionDrawer;
